import {useConfig} from '@aboutyou/config';
import {useIsBotModeEnabled} from '@aboutyou/data-fetcher';
import {GetFooterStaticResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {lazy} from '@aboutyou/lazy-imports';
import {useIsBrowsePage} from '@aboutyou/router';
import {useTrackComponent} from '@aboutyou/tracking';
import {
  COLORS,
  Disclaimer,
  Divider,
  ErrorBoundary,
  FunctionHandleNewsletterSubscribe,
  HorizontalLogoList,
  ImageListItems,
  LegalLinks,
  LinkSections,
  LinksSection,
  NewsLetterSubscriptionSkeleton,
  UspList,
  useInViewEffect,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {FC, useCallback, useMemo, useState} from 'react';
import {FooterLayoutAreas, FooterLayoutSlot} from '../FooterLayout';
import {convertLinksToTags} from '../utils/convertLinksToTags';
import {
  mapContactInfo,
  mapGroupedSectionToEntry,
  mapLinkToEntry,
  mapLinkToLegalEntry,
  mapSectionToEntry,
  mapSocialLinks,
} from '../utils/footerNormalizers';
import {ContactsSection, ContactsSectionProps} from './ContactsSection';
import {GroupedSections} from './GroupedSections';

const SubscribeToNewsletter = lazy(() =>
  import('./SubscribeToNewsletter').then(mod => ({
    default: mod.SubscribeToNewsletter,
  })),
);

const NewsLetterSubscription = lazy(
  () =>
    import(
      '@aboutyou/ui-components/src/components/organisms/NewsLetterSubscription/NewsLetterSubscription'
    ).then(mod => ({
      default: mod.NewsLetterSubscription,
    })),
  {
    fallback: <NewsLetterSubscriptionSkeleton />,
  },
);

interface Props {
  contactInfoEntries: ContactsSectionProps['contactInfoEntries'];
  usps: React.ComponentProps<typeof UspList>['items'];
  paymentProviders: ImageListItems;
  socialLinks: ContactsSectionProps['socialLinks'];
  serviceLinks: React.ComponentProps<typeof LinksSection>['links'];
  legalLinks: React.ComponentProps<typeof LegalLinks>['links'];
  copyrightText: React.ComponentProps<typeof LegalLinks>['copyrightText'];
  certificateLinks: React.ComponentProps<typeof LinksSection>['links'];
  sections: React.ComponentProps<typeof LinkSections>['linkGroups'];
  groupedSections: React.ComponentProps<typeof LinkSections>['linkGroups'];
  disclaimers: string[];
  hideNewsletterForm: boolean;
  hideCategories: boolean;
  hidePaymentInfoSection: boolean;
  uspItemsToDisplayPerSlide: number;
}

export const Footer: React.FC<Props> = ({
  usps,
  paymentProviders,
  contactInfoEntries,
  socialLinks,
  serviceLinks,
  legalLinks,
  copyrightText,
  certificateLinks,
  sections,
  groupedSections,
  disclaimers,
  uspItemsToDisplayPerSlide,
  hideCategories,
  hideNewsletterForm,
  hidePaymentInfoSection,
}) => {
  const {t} = useTranslation();
  const trackComponent = useTrackComponent();

  const isBrowsePage = useIsBrowsePage();
  const {zalandoUrl} = useConfig();
  const [subscribeData, setSubscribeData] =
    useState<Parameters<FunctionHandleNewsletterSubscribe> | null>(null);

  const onNewsletterSubscribe: FunctionHandleNewsletterSubscribe = (
    formData,
    resetForm,
  ) => {
    setSubscribeData([formData, resetForm]);

    trackComponent({
      action: 'submit',
      content_type: 'button',
      name: 'newsletter_footer',
      context: formData.gender,
    });
  };

  const resetForm = () => {
    subscribeData?.[1]();
    setSubscribeData(null);
  };

  const modifiedCopyRightText = useMemo(() => {
    if (isBrowsePage) {
      return t(
        `{copyrightText} - Part of the <a href="{zalandoUrl}">Zalando</a> Group`,
        {copyrightText, zalandoUrl},
      );
    }

    return copyrightText;
  }, [copyrightText, isBrowsePage, t, zalandoUrl]);

  return (
    <>
      <FooterLayoutSlot area={FooterLayoutAreas.STATIC.PAYMENT_SECTION}>
        {!hidePaymentInfoSection && (
          <>
            <UspList
              testId="footerUspSlider"
              itemsPerSlide={uspItemsToDisplayPerSlide}
              durationPerItemInMs={2500}
              items={usps}
              type="secondary"
            />
            <StyledHorizontalLogoList
              items={paymentProviders}
              padded
              monochrome
              dataTestId="paymentProviders"
              withBorder
            />
          </>
        )}
      </FooterLayoutSlot>

      <FooterLayoutSlot area={FooterLayoutAreas.STATIC.NEWS_LETTER}>
        {!hideNewsletterForm && (
          <>
            <LazyNewsLetterSubscription
              onNewsletterSubscribe={onNewsletterSubscribe}
            />
            <Divider />
          </>
        )}
      </FooterLayoutSlot>

      <FooterLayoutSlot area={FooterLayoutAreas.STATIC.SECTION_LINKS}>
        {!hideCategories && (
          <Nav>
            <StyledLinkSections linkGroups={sections} />
          </Nav>
        )}
      </FooterLayoutSlot>

      <FooterLayoutSlot area={FooterLayoutAreas.STATIC.GROUPED_SECTIONS}>
        {!hideCategories && (
          <>
            <Nav>
              <GroupedSections groupedSections={groupedSections} />
            </Nav>
            <Divider />
          </>
        )}
      </FooterLayoutSlot>

      <FooterLayoutSlot area={FooterLayoutAreas.STATIC.FLEXIBLE_SECTIONS}>
        <FlexibleSection>
          <ContactsSection
            contactInfoEntries={contactInfoEntries}
            socialLinks={socialLinks}
          />

          <LinksSectionText
            heading={t('Customer care')}
            links={serviceLinks}
            testId="customerCareLinks"
          />
          <LinksSectionText
            heading={t('Secure shopping')}
            links={certificateLinks}
            testId="secureShoppingCertificates"
          />

          <div>
            {disclaimers.map((text, index) => (
              <StyledDisclaimer text={convertLinksToTags(text)} key={index} />
            ))}
          </div>
        </FlexibleSection>
      </FooterLayoutSlot>

      <FooterLayoutSlot area={FooterLayoutAreas.STATIC.LEGAL_LINKS}>
        <StyledLegalLinks
          links={legalLinks}
          copyrightText={modifiedCopyRightText}
        />
      </FooterLayoutSlot>

      {subscribeData && (
        <SubscribeToNewsletter
          email={subscribeData[0].email}
          gender={subscribeData[0].gender}
          resetForm={resetForm}
        />
      )}
    </>
  );
};

const LazyNewsLetterSubscription: FC<{
  onNewsletterSubscribe: FunctionHandleNewsletterSubscribe;
}> = ({onNewsletterSubscribe}) => {
  const trackComponent = useTrackComponent();
  const [inView, setInView] = useState(false);
  const handleInView = useCallback(
    (nextInView: boolean) => {
      setInView(nextInView);

      if (nextInView) {
        trackComponent({
          action: 'impression',
          content_type: 'button',
          name: 'newsletter_footer',
          context: 'female',
        });
      }
    },
    [trackComponent],
  );
  const {ref} = useInViewEffect<HTMLDivElement>(handleInView, {
    triggerOnce: true,
    rootMargin: '250px',
  });

  return (
    <div ref={ref}>
      {inView ? (
        <ErrorBoundary
          ErrorComponent={ErrorComponent}
          renderContext="footer_newsletter"
        >
          <NewsLetterSubscription
            onNewsletterSubscribe={onNewsletterSubscribe}
          />
        </ErrorBoundary>
      ) : (
        <NewsLetterSubscriptionSkeleton />
      )}
    </div>
  );
};

export interface FooterStaticViewProps {
  uspItemsToDisplayPerSlide?: number;
  hidePaymentSections?: boolean;
  hideCategories?: boolean;
  hideNewsletterForm?: boolean;
}

export const FooterStaticView: React.FC<
  GetFooterStaticResponse & FooterStaticViewProps
> = React.memo(function ({
  footer,
  hideCategories = false,
  hidePaymentSections = false,
  hideNewsletterForm = false,
  uspItemsToDisplayPerSlide = 1.5,
}) {
  const legalLinks = useFilteredLegalLinks(footer.legalLinks);

  return (
    <Footer
      usps={footer.usps}
      sections={mapSectionToEntry(footer.linksSections)}
      groupedSections={mapGroupedSectionToEntry(footer.groupedSections)}
      contactInfoEntries={mapContactInfo(footer.contactInfo)}
      paymentProviders={footer.paymentDeliveryMethods}
      socialLinks={mapSocialLinks(footer.contactInfo)}
      certificateLinks={footer.certificateLinks}
      serviceLinks={mapLinkToEntry(footer.serviceLinks)}
      legalLinks={mapLinkToLegalEntry(legalLinks)}
      copyrightText={footer.copyright}
      disclaimers={footer.disclaimers}
      hideNewsletterForm={hideNewsletterForm}
      hideCategories={hideCategories}
      hidePaymentInfoSection={hidePaymentSections}
      uspItemsToDisplayPerSlide={uspItemsToDisplayPerSlide}
    />
  );
});

const ErrorComponent = () => null;

const useFilteredLegalLinks = (
  links: GetFooterStaticResponse['footer']['legalLinks'],
) => {
  const isBotModeEnabled = useIsBotModeEnabled();
  return links.filter(link => {
    if (link.action?.$case === 'openConsentOptions' && isBotModeEnabled) {
      return false;
    }
    return true;
  });
};

const StyledLegalLinks = styled(LegalLinks)`
  margin-top: 2rem;
`;

const StyledDisclaimer = styled(Disclaimer)`
  padding: 0 1rem;
  display: block;
  text-align: left;
`;

const StyledLinkSections = styled(LinkSections)`
  flex-direction: column;
  padding: 1.5rem 1.5rem 0;
`;

const LinksSectionText = styled(LinksSection)`
  min-width: 320px;
`;

const Nav = styled.nav`
  width: 100%;
  max-width: 1024px;
  margin: auto;
`;

const FlexibleSection = styled(Nav)`
  display: flex;
  flex-wrap: wrap;
  > * {
    flex-grow: 1;
    flex-basis: 33.333%;
    box-sizing: border-box;
  }
`;

const StyledHorizontalLogoList = styled(HorizontalLogoList)`
  background-color: ${COLORS.Grey4};
  justify-content: center;
`;
